// src/screens/EventsBanners.jsx — GET/POST/PATCH/DELETE /banners
import "bootstrap-icons/font/bootstrap-icons.css";
import { useCallback, useEffect, useState } from "react";
import Swal from "sweetalert2";
import "../css/EventsBookings.css";
import useIdleLogout from "../hooks/useIdleLogout";

const LOGS_API = (process.env.REACT_APP_ADMIN_LOGS_API_URL || "").trim();
function postLog(description) {
  try {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const name = user?.user_name || user?.name || "Admin";
    const token = localStorage.getItem("access_token") || "";
    fetch(LOGS_API, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ description, user: name, admin_name: name, timestamp: new Date().toISOString() }),
    }).catch(() => {});
  } catch { }
}

const EVENTS_BASE = (process.env.REACT_APP_EVENTS_API_URL || "").replace(/\/+$/, "");
const LINK_TYPES  = ["event", "screening", "external"];

function getBearerToken() {
  const t = localStorage.getItem("access_token") || localStorage.getItem("token") || sessionStorage.getItem("access_token");
  if (t) return String(t).trim();
  try { const u=JSON.parse(localStorage.getItem("user")||"{}"); return String(u?.access_token||u?.token||"").trim(); } catch { return ""; }
}
function getAuthHeaders(json=false) {
  const h={Accept:"application/json"}; const t=getBearerToken();
  if (t) h.Authorization=`Bearer ${t}`;
  if (json) h["Content-Type"]="application/json";
  return h;
}

async function apiCall(method, path, body) {
  if (!EVENTS_BASE) throw new Error("Events API URL not configured.");
  const opts = { method, headers:getAuthHeaders(!!body), cache:"no-store" };
  if (body) opts.body=JSON.stringify(body);
  const res  = await fetch(`${EVENTS_BASE}${path}`, opts);
  const json = await res.json().catch(()=>({}));
  if (!res.ok) throw new Error(json?.message||`HTTP ${res.status}`);
  return json;
}


export default function EventsBanners() {
  useIdleLogout();

  const [banners, setBanners]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [err, setErr]             = useState("");
  const [savingId, setSavingId]   = useState(null);

  const fetchBanners = useCallback(async () => {
    if (!EVENTS_BASE) { setErr("Events API URL not configured."); return; }
    try {
      setLoading(true); setErr("");
      const res  = await fetch(`${EVENTS_BASE}/banners`, { headers:getAuthHeaders(), cache:"no-store" });
      const json = await res.json().catch(()=>({}));
      if (!res.ok) throw new Error(json?.message||`HTTP ${res.status}`);
      setBanners(Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : []);
    } catch(e) { setErr(e.message||"Failed to load banners."); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchBanners(); }, [fetchBanners]);

  const bannerFormHtml = (b={}) => `
    <div class="row g-2 text-start" style="font-size:14px">
      <div class="col-8"><label class="form-label fw-semibold">Title *</label><input id="bn-title" class="form-control form-control-sm" value="${b.title||""}"></div>
      <div class="col-4"><label class="form-label fw-semibold">Sort Order</label><input id="bn-sort" type="number" min="0" class="form-control form-control-sm" value="${b.sort_order??0}"></div>
      <div class="col-12"><label class="form-label fw-semibold">Subtitle</label><input id="bn-sub" class="form-control form-control-sm" value="${b.subtitle||""}"></div>
      <div class="col-12"><label class="form-label fw-semibold">Image URL *</label><input id="bn-img" class="form-control form-control-sm" value="${b.image_url||""}" placeholder="https://..."></div>
      <div class="col-6"><label class="form-label fw-semibold">Link Type</label><select id="bn-ltype" class="form-select form-select-sm">${LINK_TYPES.map(t=>`<option value="${t}" ${b.link_type===t?"selected":""}>${t.charAt(0).toUpperCase()+t.slice(1)}</option>`).join("")}</select></div>
      <div class="col-6"><label class="form-label fw-semibold">Link ID (event/screening)</label><input id="bn-lid" class="form-control form-control-sm" value="${b.link_id||""}"></div>
      <div class="col-12"><label class="form-label fw-semibold">External URL</label><input id="bn-lurl" class="form-control form-control-sm" value="${b.link_url||""}" placeholder="https://... (for external type)"></div>
      <div class="col-12 mt-1"><div class="form-check"><input type="checkbox" id="bn-active" class="form-check-input" ${b.is_active!==false?"checked":""}><label class="form-check-label" for="bn-active">Active</label></div></div>
    </div>`;

  const collectForm = () => {
    const title     = document.getElementById("bn-title").value.trim();
    const image_url = document.getElementById("bn-img").value.trim();
    if (!title)     { Swal.showValidationMessage("Title is required"); return false; }
    if (!image_url) { Swal.showValidationMessage("Image URL is required"); return false; }
    return {
      title, image_url,
      subtitle:   document.getElementById("bn-sub").value.trim()  || undefined,
      link_type:  document.getElementById("bn-ltype").value,
      link_id:    document.getElementById("bn-lid").value.trim()  || undefined,
      link_url:   document.getElementById("bn-lurl").value.trim() || undefined,
      sort_order: Number(document.getElementById("bn-sort").value) || 0,
      is_active:  document.getElementById("bn-active").checked,
    };
  };

  /* CREATE */
  const handleCreate = async () => {
    const { value: fd, isConfirmed } = await Swal.fire({
      title:"Add Banner", width:"680px", showCancelButton:true, confirmButtonText:"Create",
      html: bannerFormHtml(), preConfirm: collectForm,
    });
    if (!isConfirmed || !fd) return;
    try {
      setSavingId("new");
      await apiCall("POST", "/banners", fd);
      postLog(`Created banner "${fd.title}"`);
      Swal.fire({ icon:"success", title:"Banner created!", timer:1200, showConfirmButton:false });
      fetchBanners();
    } catch(e) { Swal.fire({ icon:"error", title:"Failed", text:e.message }); }
    finally { setSavingId(null); }
  };

  /* EDIT */
  const handleEdit = async (b) => {
    const { value: fd, isConfirmed } = await Swal.fire({
      title:"Edit Banner", width:"680px", showCancelButton:true, confirmButtonText:"Save",
      html: bannerFormHtml(b), preConfirm: collectForm,
    });
    if (!isConfirmed || !fd) return;
    try {
      setSavingId(b.id);
      await apiCall("PATCH", `/banners/${b.id}`, fd);
      postLog(`Updated banner "${fd.title}"`);
      Swal.fire({ icon:"success", title:"Banner updated!", timer:1200, showConfirmButton:false });
      fetchBanners();
    } catch(e) { Swal.fire({ icon:"error", title:"Failed", text:e.message }); }
    finally { setSavingId(null); }
  };

  /* TOGGLE ACTIVE */
  const handleToggle = async (b) => {
    try {
      setSavingId(b.id);
      await apiCall("PATCH", `/banners/${b.id}`, { is_active: !b.is_active });
      postLog(`${!b.is_active ? "Activated" : "Deactivated"} banner "${b.title}"`);
      fetchBanners();
    } catch(e) { Swal.fire({ icon:"error", title:"Failed", text:e.message }); }
    finally { setSavingId(null); }
  };

  /* DELETE */
  const handleDelete = async (b) => {
    const { isConfirmed } = await Swal.fire({ icon:"warning", title:`Delete "${b.title}"?`, showCancelButton:true, confirmButtonColor:"#dc3545" });
    if (!isConfirmed) return;
    try {
      setSavingId(b.id);
      await apiCall("DELETE", `/banners/${b.id}`);
      postLog(`Deleted banner "${b.title}"`);
      Swal.fire({ icon:"success", title:"Deleted!", timer:1200, showConfirmButton:false });
      fetchBanners();
    } catch(e) { Swal.fire({ icon:"error", title:"Failed", text:e.message }); }
    finally { setSavingId(null); }
  };

  const totalActive   = banners.filter(b => b.is_active).length;
  const totalInactive = banners.filter(b => !b.is_active).length;

  return (
    <>
      {err && <div className="alert alert-danger">{err}</div>}

      {/* METRICS */}
      <div className="row row-cols-1 row-cols-md-2 row-cols-lg-4 g-3 mb-4 events-metrics">
        {[
          { label: "Total Banners",  value: loading ? "…" : banners.length },
          { label: "Active",         value: loading ? "…" : totalActive },
          { label: "Inactive",       value: loading ? "…" : totalInactive },
          { label: "Last Updated",   value: loading ? "…" : banners.length ? new Date(Math.max(...banners.map(b=>new Date(b.created_at||0)))).toLocaleDateString() : "—" },
        ].map((m,i)=>(
          <div className="col" key={i}>
            <div className="card metrics-card h-100">
              <div className="card-body">
                <h6 className="text-secondary mb-1">{m.label}</h6>
                <div className="fs-2 fw-bold">{m.value}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* TABLE */}
      <div className="bookings-table-card card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Banners</h5>
          <button className="ev-header-btn" onClick={handleCreate} disabled={savingId==="new"}>
            <i className="bi bi-plus-lg"/>Add Banner
          </button>
        </div>

        {loading && <div className="text-center py-5"><span className="spinner-border"/></div>}

        {!loading && (
          <div className="table-responsive">
            <table className="table table-hover align-middle table-pro">
              <colgroup>
                <col style={{width:"60px"}}/><col style={{width:"80px"}}/><col style={{width:"260px"}}/>
                <col style={{width:"200px"}}/><col style={{width:"80px"}}/><col style={{width:"80px"}}/>
                <col style={{width:"130px"}}/>
              </colgroup>
              <thead>
                <tr>
                  <th>SI.NO</th><th>Image</th><th>Title / Subtitle</th>
                  <th>Link</th><th>Sort</th><th>Active</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {banners.length===0 && <tr><td colSpan={7} className="text-center py-4 text-muted">No banners yet.</td></tr>}
                {banners.map((b,i)=>(
                  <tr key={b.id}>
                    <td>{i+1}</td>
                    <td>
                      {b.image_url
                        ? <img src={b.image_url} alt="" style={{width:52,height:32,objectFit:"cover",borderRadius:4}}/>
                        : <span className="text-muted">—</span>}
                    </td>
                    <td>
                      <div className="fw-semibold" style={{fontSize:13}}>{b.title}</div>
                      {b.subtitle && <div className="text-muted" style={{fontSize:11}}>{b.subtitle}</div>}
                    </td>
                    <td>
                      <span className="badge bg-secondary me-1">{b.link_type||"—"}</span>
                      <span style={{fontSize:12}}>{b.link_id||b.link_url||"—"}</span>
                    </td>
                    <td className="mono">{b.sort_order??0}</td>
                    <td>
                      <div className="form-check form-switch mb-0">
                        <input className="form-check-input" type="checkbox" checked={!!b.is_active}
                          disabled={savingId===b.id} onChange={()=>handleToggle(b)} />
                      </div>
                    </td>
                    <td>
                      <div className="ev-actions">
                        <button className="ev-btn ev-btn-edit" title="Edit" disabled={!!savingId} onClick={()=>handleEdit(b)}>
                          <i className="bi bi-pencil"/>
                        </button>
                        <button className="ev-btn ev-btn-delete" title="Delete" disabled={!!savingId} onClick={()=>handleDelete(b)}>
                          {savingId===b.id ? <span className="spinner-border spinner-border-sm"/> : <i className="bi bi-trash"/>}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <footer className="dashboard-footer mt-auto">
        <small>© {new Date().getFullYear()} <strong>སྟབས་བདེ། </strong> Admin • All rights reserved</small>
      </footer>
    </>
  );
}
